import { useCallback, useState } from 'react';

import { getEndpointUrl } from 'api';
import type {
	GeneralValidationError,
	StorageType,
	StoredFileResponse,
} from 'models/api';
import { fetchResult } from 'utils/fetch';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

export function useFileUpload({ storageType }: { storageType: StorageType }) {
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<string[] | undefined>(undefined);
	const [uploadedFiles, setUploadedFiles] = useState<StoredFileResponse[]>([]);
	const { t } = useI18n();

	const uploadFile = useCallback(
		async (file: File | undefined) => {
			setErrors(undefined);
			if (!file) {
				return;
			}
			const formData = new FormData();
			formData.append('file', file);
			setIsLoading(true);

			const res = await fetchResult<StoredFileResponse>(
				getEndpointUrl({
					name: 'PostUserFileStorage',
					pathParams: { storageType },
				}),
				{
					method: 'POST',
					body: formData,
					headers: {
						'Content-Type': 'multipart/form-data',
						'accept': 'application/json',
					},
				},
			);
			setIsLoading(false);
			if (res.isSuccess() && res.value) {
				setUploadedFiles([res.value]);
			} else if (res.isFailure('ResponseError') && res.error.status === 413) {
				setErrors([t('general_file_size_validation_text')]);
			} else if (res.isFailure('ValidationError')) {
				setErrors(
					[
						...(Object.values<GeneralValidationError[]>(res.error.fieldErrors)
							.flat()
							.map((error) => error.text) ?? []),
						...(res.error.generalErrors?.map((error) => error.text) ?? []),
					].filter(is.truthy),
				);
			} else {
				setErrors([t('general_critical_error_text')]);
			}
		},
		[storageType, t],
	);

	return {
		uploadFile,
		uploadedFiles,
		isLoading,
		errors,
		hasError: is.arrayWithLength(errors),
	};
}
